import {
  FormController,
  FormFieldsConfiguration,
  useFormController,
} from '@euler/lib/form/controller';
import { TaskListSearchParams } from '@euler/services';
import { useCreation, usePrevious } from '@euler/utils/hooks';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { getAllCriterionProviders } from './search-criteria/factory';
import { SearchCriteria } from './search-criteria/types';
import {
  searchCriteriaEquals,
  searchCriteriaToSearchParams,
  searchFormToCriteria,
} from './search-criteria/utils';

export type SearchCriteriaContextProps = {
  controller: FormController<SearchCriteria>;
  query$: BehaviorSubject<string>;
  searchParams$: BehaviorSubject<TaskListSearchParams | undefined>;
  apply: (critera: SearchCriteria) => void;
  reset: () => void;
};

export const SearchCriteriaContext = createContext<SearchCriteriaContextProps>(
  null as any,
);

export const useSearchCriteriaContext = () => {
  return useContext(SearchCriteriaContext);
};

export const SearchCriteriaContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const query$ = useCreation(() => new BehaviorSubject(''), []);

    const searchParams$ = useCreation(
      () => new BehaviorSubject<TaskListSearchParams | undefined>(undefined),
      [],
    );

    const getInitialValues = useCallback(() => {
      const res: FormFieldsConfiguration<SearchCriteria> = {} as any;
      for (const provider of getAllCriterionProviders()) {
        res[provider.type] = { defaultValue: provider.defaultValue } as any;
      }
      return res;
    }, []);

    const initialValues = useMemo<FormFieldsConfiguration<SearchCriteria>>(
      () => getInitialValues(),
      [getInitialValues],
    );

    const controller = useFormController<SearchCriteria>(initialValues);

    const reset = useCallback(() => {
      const keys = Object.keys(initialValues) as (keyof SearchCriteria)[];
      for (const key of keys) {
        controller.form[key].update(initialValues[key]?.defaultValue as any);
      }
    }, [controller.form, initialValues]);

    const apply = useCallback(
      (criteria: SearchCriteria) => {
        for (const provider of getAllCriterionProviders()) {
          controller.form[provider.type].update(criteria[provider.type] as any);
        }
      },
      [controller.form],
    );

    const value = useMemo(
      () => ({ controller, reset, apply, query$, searchParams$ }),
      [controller, reset, apply, query$, searchParams$],
    );

    const previous = usePrevious(controller.form);

    useEffect(() => {
      if (previous != null) {
        const [curr, prev] = [
          searchFormToCriteria(controller.form),
          searchFormToCriteria(previous),
        ];
        if (!searchCriteriaEquals(prev, curr)) {
          searchParams$.next(searchCriteriaToSearchParams(curr));
        }
      }
    }, [controller.form, previous, searchParams$]);

    return (
      <SearchCriteriaContext.Provider value={value}>
        {children}
      </SearchCriteriaContext.Provider>
    );
  },
);
